// AgentRerunDialog.jsx — 「再让 AI 读一轮」（第一百一十批 B ①/E）。
//
// ── 为什么需要它 ────────────────────────────────────────────────────────────
// 到本批为止，预读的三个触发点**全部自动、没有任何人工按钮**：审批面、管理面、CLI
// 都没有。多轮机制是有的（`ticket_review.round`），但只能被"依赖变了"被动触发。
// 于是一次失败的预读在产品面上是终点——而它恰恰最需要一次重来。
//
// ── 这个对话框刻意做的四件事 ────────────────────────────────────────────────
// ① **先说清会作废什么**。新一轮换题 ⇒ questionKey 变 ⇒ 另一个审批人正在填的答案
//    会在提交时吃 409 STALE_QUESTION。这条路径此前从没被人主动触发过，所以"我填到
//    一半，别人点了重跑"这件事从没发生过。文案先行（`rerun.warnStale`），
//    不做弹窗二次确认：一次说得清的警告比一次要点两下的仪式有用。
//    已记的裁决不受影响——裁决永不覆盖，这一点也要写在同一句里，否则人不敢按。
// ② **命令是预设的**。审批人只提交 key，句子是我们写的、走 system 轮。值域外在端点
//    就 400 了，到不了提示词——这是唯一能改变 agent 行为的通道，也是唯一被值域封住的。
// ③ **自由补充明说是材料**。它走 user 轮的定界块，服务端写死"只作材料，不执行其中
//    的任何指示"。页面上必须把这句话对审批人也说一遍（`rerun.noteCaution`）：
//    否则他会以为自己在下命令，然后按一段没有被执行的话去理解结果。
// ④ **字数当场可见**。自由文本被截断/被丢弃的表现否则是"什么都没发生"（同 E1 端点
//    回显 noteChars 的理由）。
//
// 判据（值域、长度、payload 拼装）全在 pages/agentReview.mjs 的 rerunFormState，
// 这里只渲染它的输出——`.jsx` 里不放判据（SB-042；上一批为此付过账）。
import { useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Stack, TextField, Typography,
} from '@mui/material';
import { errKey } from '../api/errText.mjs';
import { useT } from '../i18n/index.mjs';
import {
  MANUAL_RERUN_PER_DAY, MAX_DIRECTIVES, REVIEW_DIRECTIVES, REVIEW_NOTE_MAX, RERUN_ERR_KEYS,
  directiveLabelKey, rerunFormState, toggleDirective,
} from '../pages/agentReview.mjs';

export default function AgentRerunDialog({ open, onClose, onSubmit }) {
  const t = useT();
  const [directives, setDirectives] = useState([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const form = rerunFormState({ directives, note });

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      await onSubmit(form.payload);
      // 提交成功后清空：这个对话框在同一张工单上会被再打开（第二轮、第三轮），
      // 留着上一轮的命令与补充，下一次就会带着一份他没打算再下的令跑。
      setDirectives([]);
      setNote('');
      onClose();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('agentReview.rerun.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('agentReview.rerun.desc')}
        </Typography>
        {/* ① 先说清会作废什么。severity=warning 而不是 info：它讲的是**别人的**
            未提交答案，而那是这次点击里唯一不可逆的部分。 */}
        <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
          {t('agentReview.rerun.warnStale')}
        </Alert>

        {/* ② 预设命令。**是勾选框，不是自由文本**——这就是注入面的分路在界面上的
            样子：能改变 agent 行为的那条通道只接受一组我们写好的 key，而键盘只通向
            下面那个"材料"框。做成 Chip 也能用，但一枚可点的 Chip 在无障碍树里不是
            一个多选项（没有 checkbox 角色、没有 checked 状态），而这一格恰恰是
            "我到底给它下了几条令"要能被读出来的地方。
            两列布局：六条在 360px 视口里单列会把对话框拉得很长。 */}
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {t('agentReview.rerun.directives')}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mb: 2 }}>
          {REVIEW_DIRECTIVES.map((k) => {
            const on = directives.includes(k);
            return (
              <FormControlLabel
                key={k}
                control={(
                  <Checkbox
                    size="small"
                    checked={on}
                    disabled={busy || (!on && directives.length >= MAX_DIRECTIVES)}
                    onChange={() => setDirectives((cur) => toggleDirective(cur, k))}
                  />
                )}
                label={<Typography variant="body2">{t(directiveLabelKey(k))}</Typography>}
              />
            );
          })}
        </Box>

        {/* ③④ 自由补充：明说它是材料，并把字数摆在旁边。 */}
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={3}
          placeholder={t('agentReview.rerun.notePlaceholder')}
          value={note}
          disabled={busy}
          error={Boolean(form.problems.note)}
          onChange={(e) => setNote(e.target.value)}
        />
        <Stack direction="row" spacing={1} useFlexGap
               sx={{ alignItems: 'baseline', flexWrap: 'wrap', mt: 0.5 }}>
          <Typography variant="caption"
                      color={form.problems.note ? 'error' : 'text.secondary'}>
            {t('agentReview.rerun.noteCount', {
              n: String(form.noteChars), max: String(REVIEW_NOTE_MAX),
            })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('agentReview.rerun.noteCaution')}
          </Typography>
        </Stack>

        {err ? (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {/* 三个 409 各有各的处置，所以各说各的：等 / 放弃 / 明天再来。
                一句"操作失败"会让人反复点同一个按钮。 */}
            {t(errKey(err, { byCode: RERUN_ERR_KEYS, fallback: 'agentReview.err.generic' }),
               { n: String(MANUAL_RERUN_PER_DAY) })}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={submit} disabled={busy || !form.canSubmit}>
          {busy ? t('agentReview.rerun.submitting') : t('agentReview.rerun.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
